// Turning and mirroring move every mark and the crop with the picture (coordinates are 0..1).
import type { EditState, Mark, Rect } from "./editState.ts";

type Point = [number, number];

function mapMark(m: Mark, p: (q: Point) => Point, r: (q: Rect) => Rect): Mark {
  switch (m.kind) {
    case "stroke":
      return { ...m, points: m.points.map(p) };
    case "shape":
      return { ...m, from: p(m.from), to: p(m.to) };
    case "label":
      return { ...m, at: p(m.at) };
    case "redact":
      return { ...m, rect: r(m.rect) };
  }
}

/** A quarter turn clockwise, as seen. The renderer mirrors after turning, so a mirrored picture turns the other way round. */
export function rotate(s: EditState): EditState {
  const p = ([x, y]: Point): Point => [1 - y, x];
  const r = (q: Rect): Rect => ({ x: 1 - q.y - q.h, y: q.x, w: q.h, h: q.w });
  const rotation = ((s.rotation + (s.flipped ? 270 : 90)) % 360) as EditState["rotation"];
  return { ...s, rotation, crop: r(s.crop), marks: s.marks.map((m) => mapMark(m, p, r)) };
}

/** Mirrored left to right, as seen. */
export function flip(s: EditState): EditState {
  const p = ([x, y]: Point): Point => [1 - x, y];
  const r = (q: Rect): Rect => ({ ...q, x: 1 - q.x - q.w });
  return { ...s, flipped: !s.flipped, crop: r(s.crop), marks: s.marks.map((m) => mapMark(m, p, r)) };
}

export function normalize(a: Point, b: Point): Rect {
  return { x: Math.min(a[0], b[0]), y: Math.min(a[1], b[1]), w: Math.abs(b[0] - a[0]), h: Math.abs(b[1] - a[1]) };
}

export const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
